// Bridge to the Electron shell (desktop/preload.cjs exposes window.atriumDesktop).
// In a plain browser the badge is skipped and notifications go through lib/notify.ts instead.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Channel, Comment, Message, Notification as AppNotification } from '@shared/types';
import { useStore, type Tables } from './store';
import { chatPrefsOf, extractMentions, followsThread } from './actions';
import { claimNotifications, showBrowserNotification } from './notify';
import { notificationsMuted, useUi } from './ui';
import { personIcon } from './personIcon';

interface DesktopBridge {
  platform: string;
  isDesktop?: boolean;
  versions?: { electron?: string; chrome?: string };
  setBadgeCount: (n: number) => void;
  /** `icon`: a PNG data URL (the sender's picture), else the app icon */
  notify: (n: { title: string; body: string; path?: string; icon?: string }) => void;
  /** deep links, menu items and notification clicks ask the app to navigate */
  onNavigate?: (cb: (path: string) => void) => () => void;
  /** Windows: colors for the window buttons drawn over the top bar (lib/ui.ts applyTheme) */
  setTitleBarColors?: (colors: { color: string; symbolColor: string }) => void;
  /** Windows: the app menu's names (File, Edit, …) for the top bar's menu row; missing in older builds */
  getAppMenuLabels?: () => Promise<string[]>;
  /** Windows: open one app menu (an index into getAppMenuLabels), or all of them, at a point */
  showAppMenu?: (x: number, y: number, index?: number) => void;
  /** the app's notifications switch (also Window menu → Desktop Notifications); missing in older builds */
  getNotifications?: () => Promise<boolean>;
  /** turning it on shows a sample notification; resolves to the new state */
  setNotifications?: (on: boolean) => Promise<boolean>;
  onNotificationsChanged?: (cb: (on: boolean) => void) => () => void;
}

declare global {
  interface Window {
    atriumDesktop?: DesktopBridge;
  }
}

export const desktop = (): DesktopBridge | undefined => window.atriumDesktop;

/** The desktop app's notifications switch, kept in sync with its Window menu. null until it answers. */
export function useDesktopNotifications(): [boolean | null, (on: boolean) => void] {
  const [on, setOn] = useState<boolean | null>(null);
  useEffect(() => {
    const bridge = desktop();
    if (!bridge?.getNotifications) return;
    let live = true;
    void bridge.getNotifications().then((v) => {
      if (live) setOn(v);
    });
    const off = bridge.onNotificationsChanged?.(setOn);
    return () => {
      live = false;
      off?.();
    };
  }, []);
  const set = (next: boolean) => {
    setOn(next);
    void desktop()?.setNotifications?.(next).then(setOn);
  };
  return [on, set];
}

function describe(db: Tables, n: AppNotification): { title: string; body: string } {
  const actor = db.users[n.actorId]?.name ?? 'Someone';
  const task = n.taskId ? db.tasks[n.taskId] : undefined;
  const snippet = typeof n.data?.snippet === 'string' ? n.data.snippet : '';
  const channel = n.data?.channelId ? db.channels[String(n.data.channelId)] : undefined;
  const title = task?.name ?? (channel ? (channel.kind === 'channel' ? `#${channel.name}` : 'Direct message') : 'Atrium');
  const verbs: Record<AppNotification['kind'], string> = {
    assigned: 'assigned you',
    mention: `mentioned you: ${snippet}`,
    comment: `commented: ${snippet}`,
    reply: `replied: ${snippet}`,
    status: 'changed the status',
    due_date: 'changed the due date',
    priority: 'changed the priority',
    blocked: 'marked a blocker',
    assigned_comment: `assigned you a comment: ${snippet}`,
  };
  return { title, body: `${actor} ${verbs[n.kind] ?? 'updated a task'}` };
}

/**
 * Through the desktop app when it's there, else as a browser notification. `from` (a user id) shows
 * that person's picture instead of the app icon: chat messages, comments, mentions and reactions.
 */
function raise(n: { tag: string; title: string; body: string; path: string; from?: string }, go: (path: string) => void) {
  if (notificationsMuted(useUi.getState())) return; // account menu → Mute notifications
  const send = (icon?: string) => {
    const bridge = desktop();
    if (bridge) bridge.notify({ title: n.title, body: n.body, path: n.path, icon });
    else showBrowserNotification({ tag: n.tag, title: n.title, body: n.body, icon, onClick: () => go(n.path) });
  };
  if (!n.from) return send();
  void personIcon(useStore.getState().db.users[n.from]).then(send);
}

// inbox items that are someone talking to you get their picture; status / date / assignment changes keep the app icon
const PERSONAL_KINDS = new Set<AppNotification['kind']>(['mention', 'comment', 'reply', 'assigned_comment']);

/** Plain text of message HTML. DOMParser builds an inert document: no images load, no handlers run. */
function textOf(html: string, max = 200) {
  const text = (new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '').replace(/\s+/g, ' ').trim();
  return text.length > max ? `${text.slice(0, max - 1)}…` : text || 'Sent a file';
}

/**
 * The notification title for a new chat message, or null when it shouldn't notify. Replies notify in
 * threads I follow (I started it, replied in it or was mentioned in it), and every DM reply when that
 * DM tells me about all messages. Other messages notify when the conversation is set to all messages
 * (the default). @mentions are left to the inbox item they create.
 */
function chatTitle(db: Tables, m: Message, me: string): string | null {
  if (m.authorId === me || extractMentions(m.body).includes(me)) return null;
  const ch = db.channels[m.channelId];
  if (!ch || !ch.memberIds.includes(me)) return null;
  const { notify } = chatPrefsOf(db, me, ch);
  const author = db.users[m.authorId]?.name ?? 'Someone';
  if (m.parentId) {
    if (!followsThread(db, m.parentId, me) && !(ch.kind === 'dm' && notify === 'all')) return null;
    return ch.kind === 'dm' ? `${author} replied in a thread` : `${author} replied in #${ch.name}`;
  }
  if (notify !== 'all') return null;
  if (ch.kind === 'dm') return ch.memberIds.length > 2 ? `${author} (group message)` : author;
  return `${author} in #${ch.name}`;
}

/** Muted from the conversation's settings (bell menu), for now or until unmuted. */
function chatMuted(db: Tables, me: string, channelId: unknown) {
  const ch = typeof channelId === 'string' ? db.channels[channelId] : undefined;
  return !!ch && !!chatPrefsOf(db, me, ch).mutedUntil;
}

/** Already looking at it: the conversation (and for a reply, its thread) is open in a focused window. */
function viewing(ch: Channel, m: Message) {
  if (document.visibilityState !== 'visible' || !document.hasFocus()) return false;
  if (window.location.pathname !== `/${ch.workspaceId}/chat/${ch.id}`) return false;
  return !m.parentId || new URLSearchParams(window.location.search).get('thread') === m.parentId;
}

/** Already looking at it: the task is open (?t=) in a focused window. */
function viewingTask(taskId: string) {
  return document.visibilityState === 'visible' && document.hasFocus() && new URLSearchParams(window.location.search).get('t') === taskId;
}

// chat history loaded later, or messages missed while the computer slept, don't notify
const CHAT_NOTIFY_WINDOW = 10 * 60_000;

/** Other people's reactions on my own messages and comments: `messages:<id>` → `<emoji>\n<userId>`. */
type ReactionSnapshot = Map<string, Set<string>>;
function myReactions(db: Tables, me: string): ReactionSnapshot {
  const out: ReactionSnapshot = new Map();
  for (const c of ['messages', 'comments'] as const)
    for (const item of Object.values(db[c]) as (Message | Comment)[]) {
      if (item.authorId !== me || !item.reactions) continue;
      const pairs = new Set<string>();
      for (const [emoji, users] of Object.entries(item.reactions)) for (const u of users) if (u !== me) pairs.add(`${emoji}\n${u}`);
      out.set(`${c}:${item.id}`, pairs);
    }
  return out;
}

/**
 * Keeps the taskbar badge in sync and raises system notifications for new inbox items, direct
 * messages / thread replies, and reactions to my messages and comments: through the desktop app when
 * it's there, else as browser notifications (if this browser allowed them).
 */
export function useDesktopIntegration(workspaceId: string) {
  const me = useStore((s) => s.meId);
  const notifications = useStore((s) => s.db.notifications);
  const messages = useStore((s) => s.db.messages);
  const comments = useStore((s) => s.db.comments);
  const seen = useRef<Set<string> | null>(null);
  const seenMessages = useRef<Set<string> | null>(null);
  const reacted = useRef<ReactionSnapshot | null>(null);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => desktop()?.onNavigate?.((p) => navigate(p)), [navigate]);
  useEffect(() => {
    if (!desktop()) claimNotifications();
  }, []);

  useEffect(() => {
    const bridge = desktop();
    if (!me) {
      bridge?.setBadgeCount(0);
      seen.current = null;
      return;
    }
    const mine = Object.values(notifications).filter((n) => n.userId === me);
    const unread = mine.filter((n) => !n.readAt && !n.clearedAt && !n.laterAt && n.bucket === 'primary').length;
    bridge?.setBadgeCount(unread);

    // first run only records what already exists; afterwards notify for new primary items
    if (!seen.current) {
      seen.current = new Set(mine.map((n) => n.id));
      return;
    }
    const db = useStore.getState().db;
    for (const n of mine) {
      if (seen.current.has(n.id)) continue;
      seen.current.add(n.id);
      if (n.bucket !== 'primary' || n.readAt || chatMuted(db, me, n.data?.channelId)) continue;
      const { title, body } = describe(db, n);
      const path = n.taskId ? `/${workspaceId}/inbox?t=${n.taskId}` : n.data?.channelId ? `/${workspaceId}/chat/${String(n.data.channelId)}${n.data.messageId ? `?m=${String(n.data.messageId)}` : ''}` : `/${workspaceId}/inbox`;
      raise({ tag: n.id, title, body, path, from: PERSONAL_KINDS.has(n.kind) ? n.actorId : undefined }, (p) => navigateRef.current(p));
    }
  }, [notifications, me, workspaceId]);

  useEffect(() => {
    if (!me) {
      seenMessages.current = null;
      return;
    }
    // first run only records what already exists
    if (!seenMessages.current) {
      seenMessages.current = new Set(Object.keys(messages));
      return;
    }
    const db = useStore.getState().db;
    for (const m of Object.values(messages)) {
      if (seenMessages.current.has(m.id)) continue;
      seenMessages.current.add(m.id);
      if (Date.now() - m.createdAt > CHAT_NOTIFY_WINDOW) continue;
      const title = chatTitle(db, m, me);
      const ch = db.channels[m.channelId];
      if (!title || !ch || viewing(ch, m) || chatMuted(db, me, ch.id)) continue;
      raise({ tag: `msg:${m.id}`, title, body: textOf(m.body), path: `/${ch.workspaceId}/chat/${ch.id}?m=${m.id}`, from: m.authorId }, (p) => navigateRef.current(p));
    }
  }, [messages, me]);

  useEffect(() => {
    if (!me) {
      reacted.current = null;
      return;
    }
    const db = useStore.getState().db;
    const prev = reacted.current;
    const next = myReactions(db, me);
    reacted.current = next;
    if (!prev) return; // first run only records what already exists
    const go = (p: string) => navigateRef.current(p);
    for (const [key, pairs] of next) {
      const [c, id] = key.split(':');
      for (const pair of pairs) {
        if (prev.get(key)?.has(pair)) continue;
        const [emoji, userId] = pair.split('\n');
        const who = db.users[userId]?.name ?? 'Someone';
        const tag = `react:${id}:${emoji}:${userId}`;
        if (c === 'messages') {
          const m = db.messages[id];
          const ch = m && db.channels[m.channelId];
          if (!m || !ch || viewing(ch, m) || chatMuted(db, me, ch.id)) continue;
          const title = ch.kind === 'channel' ? `#${ch.name}` : 'Direct message';
          raise({ tag, title, body: `${who} reacted ${emoji} to your message: ${textOf(m.body, 120)}`, path: `/${ch.workspaceId}/chat/${ch.id}?m=${id}`, from: userId }, go);
        } else {
          const cm = db.comments[id];
          const task = cm && db.tasks[cm.taskId];
          if (!cm || !task || viewingTask(task.id)) continue;
          raise({ tag, title: task.name, body: `${who} reacted ${emoji} to your comment: ${textOf(cm.body, 120)}`, path: `/${task.workspaceId}/inbox?t=${task.id}`, from: userId }, go);
        }
      }
    }
  }, [messages, comments, me]);
}
